/**
 * FoodPulseHome — new classic "editorial" home for the FoodPulse
 * child site, adapted from foodpulse.net.
 *
 * Sections
 * --------
 *   1. Hero — big serif "Share a recipe. Feed a neighbour." on a
 *      warm cream field, with the FP logo overlay and two CTAs.
 *   2. Editor Highlights — 3 featured articles.
 *   3. From the community — latest 6.
 *   4. Category shelves — 4 tiles per category (Food Waste, Fruits,
 *      Vegetables, News), each with a "See all" jump.
 *   5. Twin-CTA footer strip — Businesses directory + Food encyclopedia.
 *
 * Route: /food-pulse   (dispatched from HomeRouter when site=foodpulse
 *                       OR directly at /food-pulse from any site)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Store, Utensils, Newspaper, Leaf, Apple, Trash2, Smartphone, Heart, Package, Users } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import FoodPulseSlogan from "@/components/FoodPulseSlogan";
import OwnerBusinessesStrip from "@/components/OwnerBusinessesStrip";
import SmartSearchBar from "@/components/SmartSearchBar";
import LocalDealsHero from "@/components/LocalDealsHero";
import { FP_TAGLINES } from "@/config/foodPulseTaglines";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = "/assets/foodpulse-logo.jpg";

const CATEGORIES = [
  { slug: "food-waste", label: "Food Waste", icon: Trash2,    kicker: "less waste" },
  { slug: "fruits",     label: "Fruits",     icon: Apple,     kicker: "seasonal" },
  { slug: "vegetables", label: "Vegetables", icon: Leaf,      kicker: "harvest" },
  { slug: "news",       label: "News",       icon: Newspaper, kicker: "community" },
];

function prettyDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

function TileCard({ a, size = "sm" }) {
  return (
    <Link
      to={`/food-pulse/articles/${a.slug}`}
      className={`group block ${size === "lg" ? "col-span-2" : ""}`}
      data-testid={`fp-home-tile-${a.slug}`}
    >
      {a.cover_image ? (
        <div className={`overflow-hidden bg-black/5 rounded-2xl ${size === "lg" ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
          <img src={a.cover_image} alt={a.title} loading="lazy"
               className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
        </div>
      ) : (
        <div className={`flex items-center justify-center rounded-2xl ${size === "lg" ? "aspect-[16/9]" : "aspect-[4/3]"}`}
             style={{ background: "linear-gradient(135deg,#F0E2C7,#FBF7F0)" }}>
          <Newspaper className="w-10 h-10 opacity-30" style={{ color: "#8B5E10" }} />
        </div>
      )}
      <div className="pt-3">
        {a.tag && (
          <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: "#B25D38" }}>
            {a.tag}
          </div>
        )}
        <h3 className={`font-display leading-tight ${size === "lg" ? "text-2xl" : "text-lg"}`}
            style={{ color: "#1A1613" }}>
          {a.title}
        </h3>
        {size === "lg" && a.excerpt && (
          <p className="text-sm mt-2 opacity-80 line-clamp-2">{a.excerpt}</p>
        )}
        {a.published_at && <div className="mt-2 text-xs opacity-60">{prettyDate(a.published_at)}</div>}
      </div>
    </Link>
  );
}

export default function FoodPulseHome() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [shelves, setShelves] = useState({}); // { food-waste: [...], fruits: [...], ... }
  const [loading, setLoading] = useState(true);
  const [impact, setImpact]   = useState(null);

  useEffect(() => { document.title = "Food Pulse — Community food, less waste"; }, []);

  useEffect(() => {
    // Live impact wall — refreshes counters every page load.
    axios.get(`${API}/api/foodpulse/impact-wall`)
      .then((r) => setImpact(r.data))
      .catch(() => setImpact(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // In parallel: 3 featured, 6 latest, and 4 per category shelf
        const promises = [
          axios.get(`${API}/api/food-articles`, { params: { featured: true, limit: 3 } })
               .catch(() => ({ data: { items: [] } })),
          axios.get(`${API}/api/food-articles`, { params: { limit: 6 } }),
          ...CATEGORIES.map((c) =>
            axios.get(`${API}/api/food-articles`, { params: { category: c.slug, limit: 4 } })
                 .then((r) => ({ cat: c.slug, items: r.data.items || [] }))
                 .catch(() => ({ cat: c.slug, items: [] }))
          ),
        ];
        const [feat, lat, ...shelfResults] = await Promise.all(promises);
        if (cancelled) return;
        // Fall back to latest 3 when nothing is explicitly featured.
        const featItems = feat.data.items.length ? feat.data.items : (lat.data.items || []).slice(0, 3);
        setFeatured(featItems);
        setLatest((lat.data.items || []).slice(featItems === feat.data.items ? 0 : 3, 9));
        const shelfMap = {};
        shelfResults.forEach((s) => { shelfMap[s.cat] = s.items; });
        setShelves(shelfMap);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#FBF7EF", color: "#1A2E24" }} data-testid="foodpulse-home">
      <SeoHead
        section="food-pulse"
        title="Food Pulse — Less waste. Better meals. Real deals."
        description="Food Pulse — a network for surplus produce, restaurant deals, and community food-sharing. Cut waste. Save 50–70%. Feed your neighborhood."
      />

      <OwnerBusinessesStrip />

      {/* Mobile-view swap — the mobile bundle lives at /m. Ribbon is only
          visually shown on small screens (hidden md:+). */}
      <div className="md:hidden px-4 pt-3">
        <Link
          to="/m"
          data-testid="foodpulse-view-on-mobile"
          className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-white text-sm font-semibold shadow-sm active:scale-95 transition-transform"
          style={{ background: "linear-gradient(90deg,#0F3D2E 0%,#2F6D4E 100%)" }}
        >
          <Smartphone className="w-4 h-4" />
          View the mobile / cell experience
        </Link>
      </div>

      {/* ─── MISSION-FIRST HERO ─── */}
      <section
        className="relative overflow-hidden text-white"
        style={{ background: "#0F3D2E" }}
        data-testid="fp-home-hero"
      >
        {/* Full-bleed food photography background */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(105deg, rgba(15,61,46,0.92) 0%, rgba(15,61,46,0.78) 45%, rgba(15,61,46,0.35) 100%), url('')",
            backgroundSize: "cover",
            backgroundPosition: "right center",
          }}
          aria-hidden
        />
        <div className="relative max-w-6xl mx-auto px-4 py-6 sm:py-8 lg:py-12">
          <div className="inline-flex items-center gap-2 rounded-full border-2 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.28em] mb-3"
               style={{ borderColor: "#E4A140", color: "#F6C87A" }}>
            <img src={LOGO} alt="Food Pulse" className="h-4 w-4 rounded-sm" style={{ background: "rgba(255,255,255,0.9)" }} />
            Food Pulse — Portland &amp; beyond
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight max-w-3xl">
            Less waste. <span style={{ color: "#E4A140" }}>Better meals.</span>{" "}
            <span className="italic" style={{ color: "#F6C87A" }}>Real deals.</span>
          </h1>
          <p className="mt-3 max-w-xl text-sm sm:text-base leading-relaxed opacity-90">
            Every day, thousands of pounds of perfectly good produce, bread, and prepared food get thrown out. We connect it to shoppers who want it — at <strong className="font-semibold" style={{ color: "#F6C87A" }}>50&ndash;70% off supermarket prices.</strong>
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/food-pulse/deals"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 font-semibold text-[#0F3D2E] shadow-lg hover:shadow-xl transition-shadow text-sm"
              style={{ background: "#E4A140" }}
              data-testid="fp-hero-deals"
            >
              🏷️ Today's deals near you →
            </Link>
            <Link
              to="/food-pulse/businesses"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 font-semibold border-2 hover:bg-white/10 transition-colors text-sm"
              style={{ borderColor: "#F6C87A", color: "#F6C87A" }}
              data-testid="fp-hero-vendors"
            >
              🛒 Explore vendors
            </Link>
            <Link
              to="/food-pulse/spot"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 font-semibold hover:bg-white/10 transition-colors underline underline-offset-4 text-sm"
              style={{ color: "#F6C87A" }}
              data-testid="fp-hero-post-deal"
            >
              📸 Spot a deal · earn points →
            </Link>
          </div>

          {/* Universal Smart Search — the single doorway into products,
              deals, recipes and the barcode/photo scanner. */}
          <div className="mt-5 max-w-2xl" data-testid="fp-hero-smart-search">
            <SmartSearchBar autoFocus={false} placeholder="Search products, brands or scan a barcode…" />
            <div className="mt-1.5 text-[11px] opacity-70 flex flex-wrap gap-3">
              <span>🎤 Say “organic bananas”</span>
              <span>📷 Scan a barcode</span>
              <span>✨ Photo → AI identify</span>
            </div>
          </div>
          {/* Mission stats — LIVE from the database, growing every day */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl" data-testid="fp-impact-wall">
            <ImpactStat
              value={impact?.volunteers?.total ?? "—"}
              label="Volunteers joined"
              testid="fp-impact-volunteers"
            />
            <ImpactStat
              value={impact?.opportunities?.matched ?? "—"}
              label="Opportunities matched"
              testid="fp-impact-opportunities"
            />
            <ImpactStat
              value={impact?.charities?.reachouts ?? "—"}
              label="Charity reach-outs"
              testid="fp-impact-reachouts"
            />
            <ImpactStat
              value={impact?.products?.scanned ?? "—"}
              label="Products scanned"
              testid="fp-impact-products"
            />
          </div>
          {impact?.charities?.meals_served > 0 && (
            <div className="mt-3 text-xs opacity-80 italic max-w-xl" data-testid="fp-impact-meals-note">
              Community charities have reported serving{" "}
              <strong className="font-semibold" style={{ color: "#F6C87A" }}>
                {impact.charities.meals_served.toLocaleString()}
              </strong>{" "}
              meals, helping{" "}
              <strong className="font-semibold" style={{ color: "#F6C87A" }}>
                {impact.charities.families_helped.toLocaleString()}
              </strong>{" "}
              families and rescuing{" "}
              <strong className="font-semibold" style={{ color: "#F6C87A" }}>
                {impact.charities.food_rescued_kg.toLocaleString()} kg
              </strong>{" "}
              of food.
            </div>
          )}
        </div>
      </section>

      {/* ─── LOCAL DEALS TODAY (Phase 1) ─── */}
      <LocalDealsHero limit={12} />

      {/* ─── Mission strip ─── */}
      <section className="border-b" style={{ background: "#FBF7EF", borderColor: "#DFCDA0" }}>
        <div className="max-w-6xl mx-auto px-4 py-6 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest" style={{ color: "#0F3D2E" }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#E4A140" }} />
            Live daily 3 PM Pacific
          </div>
          <span className="text-sm italic" style={{ color: "#3D5346" }}>
            Portland's first surplus-food marketplace, built by a chef who was tired of throwing food out.
          </span>
          <Link to="/food-pulse/about" className="ml-auto text-xs font-mono uppercase tracking-widest hover:underline" style={{ color: "#0F3D2E" }}>
            Our story →
          </Link>
        </div>
      </section>

      {/* ─── EDITOR HIGHLIGHTS ─── */}
      {!loading && featured.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-14" data-testid="fp-home-featured">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>— Featured</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-6" style={{ color: "#1A1613" }}>Editor highlights</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featured.slice(0, 3).map((a, i) => (
              <TileCard key={a.slug} a={a} size={i === 0 && featured.length === 3 ? "sm" : "sm"} />
            ))}
          </div>
        </section>
      )}

      {/* ─── LATEST ─── */}
      {!loading && latest.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-14 border-t" style={{ borderColor: "#E9D8B4" }}
                 data-testid="fp-home-latest">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>— Latest</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-6" style={{ color: "#1A1613" }}>From the community</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {latest.slice(0, 6).map((a) => (
              <TileCard key={a.slug} a={a} />
            ))}
          </div>
        </section>
      )}

      {/* ─── CATEGORY SHELVES ─── */}
      {!loading && CATEGORIES.map((c) => {
        const items = shelves[c.slug] || [];
        if (items.length === 0) return null;
        return (
          <section
            key={c.slug}
            className="max-w-6xl mx-auto px-4 py-14 border-t"
            style={{ borderColor: "#E9D8B4" }}
            data-testid={`fp-home-shelf-${c.slug}`}
          >
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
                     style={{ color: "#B25D38" }}>— {c.label}</div>
                <h2 className="font-display text-3xl sm:text-4xl" style={{ color: "#1A1613" }}>
                  {c.label} from the desk
                </h2>
              </div>
              <Link to={`/food-pulse/articles?category=${c.slug}`}
                    className="text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                    style={{ color: "#B25D38" }}
                    data-testid={`fp-home-shelf-more-${c.slug}`}>
                See all <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {items.map((a) => <TileCard key={a.slug} a={a} />)}
            </div>
          </section>
        );
      })}

      {loading && (
        <div className="max-w-6xl mx-auto px-4 py-24 text-center text-sm opacity-70" data-testid="fp-home-loading">
          Loading Food Pulse…
        </div>
      )}

      {/* ─── TWIN CTA STRIP ─── */}
      <section className="border-t" style={{ borderColor: "#E9D8B4", background: "#1A1613" }}
               data-testid="fp-home-cta-strip">
        <div className="max-w-6xl mx-auto px-4 py-14 grid gap-8 lg:grid-cols-2">
          <Link to="/food-pulse/businesses" className="group block"
                data-testid="fp-home-cta-directory">
            <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest mb-3"
                 style={{ color: "#EA580C" }}>
              <Store className="w-3.5 h-3.5" /> Directory
            </div>
            <div className="font-display text-3xl sm:text-4xl leading-tight text-white mb-3">
              Every US food business, <em style={{ color: "#EA580C" }}>one open directory.</em>
            </div>
            <p className="text-sm opacity-80 text-white max-w-md">
              Restaurants, cafes, bakeries, supermarkets, food trucks, farms, seafood shops, food banks and distributors. Searchable, verifiable, and open for owners to claim.
            </p>
            <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-white group-hover:gap-2 transition-all">
              Browse directory <ArrowRight className="w-4 h-4" />
            </div>
          </Link>

          <Link to="/food-pulse/food" className="group block" data-testid="fp-home-cta-encyclopedia">
            <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest mb-3"
                 style={{ color: "#EA580C" }}>
              <Utensils className="w-3.5 h-3.5" /> Encyclopedia
            </div>
            <div className="font-display text-3xl sm:text-4xl leading-tight text-white mb-3">
              Every food, <em style={{ color: "#EA580C" }}>one living reference.</em>
            </div>
            <p className="text-sm opacity-80 text-white max-w-md">
              Verified profiles for fruits, vegetables, grains, seafood, dairy — with nutrition, growing seasons, 25+ recipes each, and cultural notes drawn from USDA, FAO, WHO and NIH.
            </p>
            <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-white group-hover:gap-2 transition-all">
              Open encyclopedia <ArrowRight className="w-4 h-4" />
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}


function ImpactStat({ value, label, testid }) {
  const display = typeof value === "number" ? value.toLocaleString() : value;
  return (
    <div data-testid={testid}>
      <div className="font-display text-xl sm:text-2xl leading-none" style={{ color: "#E4A140" }}>
        {display}
      </div>
      <div className="text-[9px] font-mono uppercase tracking-widest opacity-80 mt-1">
        {label}
      </div>
    </div>
  );
}
